import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@mui/material';
import {useCallback, useMemo, useState} from 'react';
import {CloseButton} from '../common/button/CloseButton';
import {TextButton} from '../common/button/TextButton';
import {Translation} from '../i18n/Translation';
import {ComponentWithoutChildren} from '../react/types';
import {ResponsiveDialog} from '../responsive/ResponsiveDialog';
import {useAuthUser} from './hooks/useAuthUser';
import {useSendVerificationEmail} from './hooks/useSendVerificationEmail';

export const VerifyEmailBanner: ComponentWithoutChildren = () => {
  const {errorCode, errorMessage, isSending, sendVerificationEmail} = useSendVerificationEmail();
  const maybeVerificationError = useMemo(
    () => (errorCode || errorMessage ? `${errorCode} ${errorMessage}`.trim() : undefined),
    [errorCode, errorMessage],
  );
  const email = useAuthUser().data.email as string;
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState<boolean>(false);

  const sendEmail: () => Promise<void> = useCallback(async () => {
    try {
      await sendVerificationEmail();
    } finally {
      setConfirmationDialogOpen(true);
    }
  }, [sendVerificationEmail, setConfirmationDialogOpen]);

  return (
    <Container maxWidth="sm">
      <Accordion>
        <AccordionSummary
          aria-controls="verify-email-content"
          expandIcon={<ExpandMoreIcon />}
          id="verify-email-header"
          sx={{
            backgroundColor: (theme) => theme.palette.action.selected,
          }}
        >
          <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'center', width: '100%'}}>
            <Translation keys="auth:verifyEmailBanner.pleaseVerify">
              {(pleaseVerify) => (
                <Typography sx={{alignItems: 'center', display: 'flex', height: '100%'}} variant="subtitle1">
                  <MarkEmailReadIcon color="primary" sx={{mr: 1}} />
                  {pleaseVerify}
                </Typography>
              )}
            </Translation>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{py: 1}}>
          <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'center', width: '100%'}}>
            <Translation keys={['auth:verifyEmailBanner.toSendVerificationEmail']}>
              {([toSendVerificationEmail]) => (
                <Typography sx={{alignItems: 'center', display: 'flex', height: '100%'}} variant="subtitle1">
                  <TextButton
                    color="primary"
                    disabled={isSending}
                    i18nKey="common:button.clickHere"
                    loading={isSending}
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onClick={sendEmail}
                    variant="text"
                  />
                  {toSendVerificationEmail}
                </Typography>
              )}
            </Translation>
          </Box>
        </AccordionDetails>
        {maybeVerificationError ? (
          <VerificationEmailErrorDialog
            email={email}
            error={maybeVerificationError}
            open={confirmationDialogOpen}
            setOpen={setConfirmationDialogOpen}
          />
        ) : (
          <VerificationEmailSentDialog
            email={email}
            open={confirmationDialogOpen}
            setOpen={setConfirmationDialogOpen}
          />
        )}
      </Accordion>
    </Container>
  );
};

interface VerificationEmailSentDialogProps {
  email: string;
  open: boolean;
  setOpen: (open: boolean) => void;
}
const VerificationEmailSentDialog: ComponentWithoutChildren<VerificationEmailSentDialogProps> = ({
  email,
  open,
  setOpen,
}: VerificationEmailSentDialogProps) => {
  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);
  return (
    <ResponsiveDialog onClose={handleClose} open={open}>
      <Translation keys="auth:verificationEmailBanner.sentDialog.title">
        {(title) => (
          <DialogTitle sx={{alignItems: 'center', display: 'flex', flexDirection: 'row'}}>
            <MarkEmailReadIcon color="primary" sx={{mr: 1}} />
            {title}
          </DialogTitle>
        )}
      </Translation>
      <DialogContent dividers>
        <Translation
          keys={[
            'auth:verificationEmailBanner.sentDialog.emailSent',
            'auth:verificationEmailBanner.sentDialog.pleaseClick',
          ]}
        >
          {([emailSent, pleaseClick]) => (
            <DialogContentText component="div" sx={{display: 'flex', flexDirection: 'column'}}>
              <Box sx={{alignItems: 'baseline', display: 'flex', flexDirection: 'row'}}>
                {emailSent}
                <Typography sx={{fontFamily: 'monospace', fontWeight: 'bold', ml: 1}}>{email}</Typography>
              </Box>
              {pleaseClick}
            </DialogContentText>
          )}
        </Translation>
      </DialogContent>
      <DialogActions>
        <CloseButton onClick={handleClose} />
      </DialogActions>
    </ResponsiveDialog>
  );
};

interface VerificationEmailErrorDialogProps {
  email: string;
  error: string;
  open: boolean;
  setOpen: (open: boolean) => void;
}
const VerificationEmailErrorDialog: ComponentWithoutChildren<VerificationEmailErrorDialogProps> = ({
  email,
  error,
  open,
  setOpen,
}: VerificationEmailErrorDialogProps) => {
  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);
  return (
    <ResponsiveDialog onClose={handleClose} open={open}>
      <Translation keys="auth:verificationEmailBanner.errorDialog.title">
        {(title) => (
          <DialogTitle sx={{alignItems: 'center', display: 'flex', flexDirection: 'row'}}>
            <ErrorIcon color="error" sx={{mr: 1}} />
            {title}
          </DialogTitle>
        )}
      </Translation>
      <DialogContent dividers>
        <Translation keys={['auth:verificationEmailBanner.errorDialog.emailError']}>
          {(errorSendingTo) => (
            <DialogContentText component="div" sx={{display: 'flex', flexDirection: 'column'}}>
              <Box sx={{alignItems: 'baseline', display: 'flex', flexDirection: 'row'}}>
                {errorSendingTo}
                <Typography sx={{fontFamily: 'monospace', fontWeight: 'bold', ml: 1}}>{email}</Typography>
              </Box>
              <Typography sx={{fontFamily: 'monospace', fontWeight: 'bold'}}>{error}</Typography>
            </DialogContentText>
          )}
        </Translation>
      </DialogContent>
      <DialogActions>
        <CloseButton onClick={handleClose} />
      </DialogActions>
    </ResponsiveDialog>
  );
};
